import { Injectable, inject } from '@angular/core';
import { LOCAL_STORAGE } from '@core/storage/storage.tokens';
import { LOCAL_KEYS } from '@core/storage/storage.keys';
import { ATTRIBUTION_KEYS } from '@typings/app/attribution.types';
import type { AttributionSnapshot } from '@typings/app/attribution.types';

/** First-party cookie the marketing site's GTM container writes on `.shiftat.sa`. */
export const ATTRIBUTION_COOKIE = 'shiftat_attr';

/**
 * Cookies Google's Conversion Linker writes on `.shiftat.sa`, as `GCL.<seconds>.<click id>`.
 * They are what Google Ads itself attributes a conversion with, so reading them keeps the
 * sheet's click id in step with the Ads count.
 */
const GOOGLE_CLICK_COOKIES = { gclid: '_gcl_aw', gbraid: '_gcl_gb' } as const;

/**
 * Captures the ad attribution a visit arrived with and keeps it until the conversion,
 * which can be days later and several sessions away.
 *
 * Ads land on both `shiftat.sa` and `ai.shiftat.sa`, so there are two sources. The URL
 * wins when present — it describes this click. The cookie, written on the parent domain
 * by the marketing site, covers the employer who browsed there first or came back later.
 */
@Injectable({ providedIn: 'root' })
export class AttributionService {
  private readonly storage = inject(LOCAL_STORAGE);

  /**
   * Called once at bootstrap, before the Router's first navigation — a guard redirect to
   * /login drops the query string, and by then there is nothing left to read.
   */
  capture(): void {
    const captured = this.fromUrl() ?? this.fromCookie();
    // A visit with no parameters is not a new attribution — an employer returning via a
    // bookmark should keep the ad that originally brought them.
    if (captured) this.storage.set(LOCAL_KEYS.attribution, captured);
  }

  /**
   * The stored attribution, with Google's own click id filled in when the app never saw
   * one. That is the common case: an ad lands on `shiftat.sa`, whose links to the app carry
   * no query string, yet the Conversion Linker has already put the click id in a cookie the
   * app can read. Read here rather than in capture() so it reflects the cookie as it stands
   * at the conversion, the same moment Google reads it.
   */
  snapshot(): AttributionSnapshot | null {
    const stored = this.storage.get(LOCAL_KEYS.attribution);
    if (stored && (stored.gclid || stored.gbraid || stored.wbraid)) return stored;

    const gclid = this.readGoogleClickId(GOOGLE_CLICK_COOKIES.gclid);
    const gbraid = gclid ? '' : this.readGoogleClickId(GOOGLE_CLICK_COOKIES.gbraid);
    if (!gclid && !gbraid) return stored;

    return { ...(stored ?? emptySnapshot()), gclid, gbraid };
  }

  private fromUrl(): AttributionSnapshot | null {
    try {
      const params = new URL(window.location.href).searchParams;
      return toSnapshot((key) => params.get(key));
    } catch {
      return null;
    }
  }

  private fromCookie(): AttributionSnapshot | null {
    const raw = this.readCookie();
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(raw));
      if (typeof parsed !== 'object' || parsed === null) return null;
      const values = parsed as Record<string, unknown>;
      return toSnapshot((key) => (typeof values[key] === 'string' ? (values[key] as string) : null));
    } catch {
      // A cookie we can't parse is treated as absent — never as an error worth surfacing.
      return null;
    }
  }

  private readCookie(): string | null {
    return this.readCookies(ATTRIBUTION_COOKIE)[0] ?? null;
  }

  /**
   * The Linker can leave one cookie on `ai.shiftat.sa` and another on `.shiftat.sa`, and the
   * browser sends both under the same name. The newest click is the one Ads credits.
   */
  private readGoogleClickId(name: string): string {
    let newest = { at: -1, id: '' };
    for (const value of this.readCookies(name)) {
      const [prefix, seconds, ...rest] = value.split('.');
      const at = Number(seconds);
      const id = rest.join('.');
      if (prefix === 'GCL' && Number.isFinite(at) && id && at > newest.at) newest = { at, id };
    }
    return newest.id;
  }

  private readCookies(name: string): string[] {
    try {
      // `document.cookie` throws in some embedded and privacy-mode contexts, and this runs
      // during bootstrap where an exception would take the whole app down.
      return document.cookie
        .split('; ')
        .filter((c) => c.startsWith(`${name}=`))
        .map((c) => c.slice(name.length + 1))
        .filter(Boolean);
    } catch {
      return [];
    }
  }
}

/** A snapshot with every field `''`, for a click id that arrived without any utm parameters. */
function emptySnapshot(): AttributionSnapshot {
  return Object.fromEntries(ATTRIBUTION_KEYS.map((key) => [key, ''])) as unknown as AttributionSnapshot;
}

/** Builds a full snapshot from a lookup, or null when the source carried nothing at all. */
function toSnapshot(read: (key: string) => string | null): AttributionSnapshot | null {
  const snapshot = {} as AttributionSnapshot;
  let found = false;
  for (const key of ATTRIBUTION_KEYS) {
    const value = read(key);
    snapshot[key] = value ?? '';
    if (value) found = true;
  }
  return found ? snapshot : null;
}
